import type { BrushStroke, StrokeData } from "./bishun-drawing.js";
import { StrokeDemonstration } from "./xiezi-demonstration.js";
import { flashCharacter, flashLength, flyCharacter } from "./xiezi-flash.js";
import type { StrokePoints } from "../handwriting/stroke-shape.js";
import { HandwritingPad } from "./handwriting-pad.js";
import { setBrush } from "./bishun-playback.js";
import { characterLayer } from "./xiezi-layer.js";

/**
 * The square a reader writes a character in during a drill.
 *
 * Two layers in one box. Under the ink is the 词典's own 笔顺 drawing, the
 * SVG `bishun-drawing.ts` builds, which is the outline the reader traces and
 * the strokes that snap into place as they are taken. Over it is the pad the
 * 词典 search box and the 复习 card write on, which is where the ink lands.
 * Both are square and both fill the box, so a point on the pad is the same
 * point on the drawing.
 *
 * **Snapping is the brush.** A stroke the drill takes is shown by writing the
 * drawing's own brush for it, the thick line clipped to the stroke's outline
 * that the figure animates with, and clearing the reader's ink from the pad.
 * The character therefore takes shape in the typeface it is taught in,
 * however the hand wobbled, which is the help the first three stages give. The
 * fourth stage leaves the ink where it was, and `xiezi-drill.ts` marks it.
 *
 * **The drawing can be swapped for another character's.** The pad stays, with
 * its listeners, and {@link XieziSquare.show} puts a different drawing under
 * it, which is how an Exercise over several characters moves between them in
 * one square.
 *
 * **The outline and the cue are attributes on the box.** Which stage shows
 * the outline and which points out the next stroke is `xiezi-stages.ts`'s
 * business, and the stylesheet draws both off `data-cb-xiezi-outline` and
 * `data-cb-xiezi-cue`. `modules/cidian-xiezi` has the rules.
 *
 * **A stroke can be shown being written.** The cue writes itself in as it
 * moves to each stroke, and a stroke that was not taken is written in for the
 * reader to see, in every stage that snaps. `xiezi-demonstration.ts` moves the
 * brush.
 *
 * **A character written whole flashes green**, by `xiezi-flash.ts`.
 */

/** What the square is built from. */
export interface XieziSquareProperties {
  /** The box the two layers go in. */
  readonly box: HTMLElement;

  /** The canvas the ink lands on, which the markup drew inside the box. */
  readonly canvas: HTMLCanvasElement;

  /** The first character's outlines, as the file publishes them. */
  readonly data: StrokeData;

  /** Called when a stroke lands on the pad. */
  readonly onStroke: () => void;
}

/** The square, its drawing and its pad. */
export class XieziSquare {
  readonly #box: HTMLElement;
  readonly #pad: HandwritingPad;
  #drawing: Element;
  #strokes: readonly BrushStroke[] = [];
  #guides: readonly Element[] = [];
  readonly #demonstration = new StrokeDemonstration();
  #flashing: Element | undefined;
  #quiet = false;

  constructor(properties: XieziSquareProperties) {
    const { box, canvas, data, onStroke } = properties;

    this.#box = box;
    // A drawing to take out before the first goes in, which is nothing.
    this.#drawing = box.ownerDocument.createElement("div");
    this.#pad = new HandwritingPad({
      canvas,
      onChange: (): void => {
        if (!this.#quiet) {
          onStroke();
        }
      },
    });

    this.show(data);
    this.#pad.resize();
  }

  /** How many strokes the character has. */
  get strokeCount(): number {
    return this.#strokes.length;
  }

  /** The strokes on the pad, in CSS pixels of the box. */
  get ink(): readonly StrokePoints[] {
    return this.#pad.strokes;
  }

  /** The side of the box, in CSS pixels, which the ink is scaled by. */
  get side(): number {
    return this.#box.getBoundingClientRect().width;
  }

  /**
   * Put a character's drawing under the pad, in place of the one there.
   *
   * Every stroke starts unwritten, and the ink is cleared, since it was the
   * last character's.
   * @param data - The character's outlines.
   */
  show(data: StrokeData): void {
    const layer = characterLayer(this.#box.ownerDocument, data);

    this.#drawing.remove();
    this.#drawing = layer.drawing;
    this.#strokes = layer.strokes;
    this.#guides = layer.guides;
    this.#demonstration.reset(layer.demos);
    this.#box.prepend(layer.drawing);
    this.clearInk();
  }

  /**
   * Refuse ink, or take it again, which is how a finished drill stays still
   * until the reader starts it again. The box carries
   * `data-cb-xiezi-locked` while it refuses, for the stylesheet.
   * @param locked - Whether to refuse ink.
   */
  lock(locked: boolean): void {
    this.#pad.locked = locked;
    this.#box.toggleAttribute("data-cb-xiezi-locked", locked);
  }

  /** Fit the pad to its box again, after the box has changed size. */
  resize(): void {
    this.#pad.resize();
  }

  /**
   * Show the first `count` strokes written, and the rest not.
   *
   * @param count - How many strokes of the character are in place.
   */
  showWritten(count: number): void {
    this.#strokes.forEach((stroke, at) => {
      setBrush(stroke, at < count);
    });
  }

  /** Show or hide the outline under the ink. */
  showOutline(state: "shown" | "hidden"): void {
    this.#box.dataset["cbXieziOutline"] = state;
  }

  /**
   * Point out one stroke of the outline, or none.
   *
   * @param at - Which stroke, or nothing to point out none.
   */
  cue(at: number | undefined): void {
    this.#guides.forEach((guide, index) => {
      guide.toggleAttribute("data-cb-xiezi-cue", index === at);
    });

    this.#demonstration.cue(at);
  }

  /**
   * Write one stroke in the cue's blue, as a brush moving along it, for a
   * reader who drew another.
   * @param at - Which stroke.
   */
  demonstrate(at: number): void {
    this.#demonstration.demonstrate(at);
  }

  /**
   * Flash the character green, whole, over whatever the square shows next,
   * send a copy into the pie it fills, and hold the cue back until both have
   * played.
   * @param into - The wedge the pass fills, where there are pies.
   */
  flash(into?: Element): void {
    this.#flashing?.remove();
    this.#flashing = flashCharacter(this.#drawing);
    this.#demonstration.pause(flashLength);
    flyCharacter(this.#drawing, into);
  }

  /**
   * Take the last stroke of ink off the pad without saying so.
   *
   * The drill reads a stroke as it lands and then clears it, in the stages
   * where the brush stands in for it. The pad announces an undo the way it
   * announces a stroke, and the drill would otherwise read the clearing as a
   * second stroke.
   */
  dropLastInk(): void {
    this.#quiet = true;
    this.#pad.undo();
    this.#quiet = false;
  }

  /** Take every stroke of ink off the pad, without saying so. */
  clearInk(): void {
    this.#quiet = true;
    this.#pad.clear();
    this.#quiet = false;
  }
}
